<template>
  <div class="invalid-objects">
    <div class="invalid-objects__header">
      <div class="invalid-objects__title">Инвалидные объекты</div>
      <div class="invalid-objects__actions" v-if="canShow">
        <q-btn
          color="primary"
          outline
          icon="refresh"
          label="Обновить"
          :disable="activeLoading || exporting"
          @click="refreshActiveTab"
        />
        <q-btn
          class="q-ml-sm"
          color="positive"
          outline
          icon="file_download"
          label="Excel"
          :loading="exporting"
          :disable="activeLoading || exporting"
          @click="exportActiveTab"
        />
      </div>
    </div>

    <CustomLoader v-if="!hasUserData" />

    <q-banner v-if="hasUserData && !canShow" class="invalid-objects__banner bg-grey-2 text-grey-9" rounded>
      <template v-slot:avatar>
        <q-icon name="lock" />
      </template>
      Нет прав для просмотра. Требуется право <b>is_show_db_func</b>.
    </q-banner>

    <q-banner v-else-if="hasUserData && canShow && errorText" class="invalid-objects__banner bg-red-1 text-red-10" rounded>
      <template v-slot:avatar>
        <q-icon name="error_outline" />
      </template>
      <div class="invalid-objects__error">
        <div class="invalid-objects__error-title">Ошибка загрузки</div>
        <div class="invalid-objects__error-text">{{ errorText }}</div>
      </div>
    </q-banner>

    <div v-if="hasUserData && canShow" class="invalid-objects__content">
      <q-tabs
        v-model="activeTab"
        class="text-grey-8"
        active-color="primary"
        indicator-color="primary"
        align="left"
        narrow-indicator
      >
        <q-tab name="invalid" label="Инвалидные объекты" />
        <q-tab name="fk" label="FK (таблицы)" />
        <q-tab name="trg" label="Триггеры (таблицы)" />
        <q-tab name="stat" label="Статистика вызовов" />
      </q-tabs>

      <q-separator />

      <q-tab-panels v-model="activeTab" animated keep-alive>
        <q-tab-panel name="invalid">
          <CustomLoader v-if="invalid.loading && !invalid.rows.length" />
          <CustomTable
            v-else
            :columns="invalid.columns"
            :rowsData="invalid.rows"
            :loading="invalid.loading"
            title="Ошибки компиляции / инвалидные объекты"
            :showSearch="true"
            :allWidth="true"
            :stickyHeader="false"
            :bordered="true"
            rowKey="__row_key"
          />
        </q-tab-panel>

        <q-tab-panel name="fk">
          <CustomLoader v-if="fk.loading && !fk.rows.length" />
          <CustomTable
            v-else
            :columns="fk.columns"
            :rowsData="fk.rows"
            :loading="fk.loading"
            title="read_table_for_create_foreign_key"
            :showSearch="true"
            :allWidth="true"
            :stickyHeader="false"
            :bordered="true"
            rowKey="__row_key"
          />
        </q-tab-panel>

        <q-tab-panel name="trg">
          <CustomLoader v-if="trg.loading && !trg.rows.length" />
          <CustomTable
            v-else
            :columns="trg.columns"
            :rowsData="trg.rows"
            :loading="trg.loading"
            title="read_table_for_create_trigger"
            :showSearch="true"
            :allWidth="true"
            :stickyHeader="false"
            :bordered="true"
            rowKey="__row_key"
          />
        </q-tab-panel>

        <q-tab-panel name="stat">
          <div class="invalid-objects__stat-controls">
            <q-input
              v-model="stat.func_name"
              dense
              outlined
              clearable
              label="func_name (пусто = сегодня)"
              @keyup.enter="loadStat(true)"
            />
            <q-btn
              color="primary"
              outline
              icon="search"
              label="Показать"
              :disable="stat.loading"
              @click="loadStat(true)"
            />
          </div>

          <CustomLoader v-if="stat.loading && !stat.rows.length" />
          <CustomTable
            v-else
            :columns="stat.columns"
            :rowsData="stat.rows"
            :loading="stat.loading"
            title="func_stat_tab"
            :showSearch="true"
            :allWidth="true"
            :stickyHeader="false"
            :bordered="true"
            rowKey="__row_key"
          />
        </q-tab-panel>
      </q-tab-panels>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import CustomLoader from '@/components/common/CustomLoader'
import CustomTable from '@/components/common/CustomTable'
import { useAppStore } from '@/services/store/app'
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar'
import { getFuncStat, getInvalidObjects, getTableForCreateForeignKey, getTableForCreateTrigger } from '@/views/InvalidObjects/services'

const appStore = useAppStore()
const { userData } = storeToRefs(appStore)
const { showErrorSnackbar } = useCustomSnackbar()

const hasUserData = computed(() => userData.value !== null)
const canShow = computed(() => !!userData.value?.is_show_db_func)

const errorText = ref('')
const activeTab = ref('invalid')
const exporting = ref(false)

const buildColumns = (data) => {
  const first = Array.isArray(data) && data.length ? data[0] : null
  if (!first || typeof first !== 'object') return []

  const keys = Object.keys(first).filter((k) => !k.startsWith('__'))
  return keys.map((key) => ({
    name: key,
    label: key,
    field: key,
    align: 'left',
    sortable: true,
  }))
}

const normalizeRows = (data) => {
  if (!Array.isArray(data)) return []
  return data.map((r, idx) => ({
    __row_key: r?.id ?? r?.object_id ?? r?.oid ?? r?.func_oid ?? r?.row_id ?? idx,
    ...(r && typeof r === 'object' ? r : { value: r }),
  }))
}

const invalid = ref({ loading: false, loaded: false, rows: [], columns: [] })
const fk = ref({ loading: false, loaded: false, rows: [], columns: [] })
const trg = ref({ loading: false, loaded: false, rows: [], columns: [] })
const stat = ref({ loading: false, loaded: false, func_name: '', rows: [], columns: [] })

const activeLoading = computed(() => {
  if (activeTab.value === 'invalid') return invalid.value.loading
  if (activeTab.value === 'fk') return fk.value.loading
  if (activeTab.value === 'trg') return trg.value.loading
  if (activeTab.value === 'stat') return stat.value.loading
  return false
})

const loadInvalid = async (force = false) => {
  if (!canShow.value) return
  if (invalid.value.loaded && !force) return
  invalid.value.loading = true
  errorText.value = ''

  try {
    const data = await getInvalidObjects()
    const normalized = normalizeRows(data || [])
    invalid.value.rows = normalized
    invalid.value.columns = buildColumns(normalized)
    invalid.value.loaded = true
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'Ошибка при загрузке'
    errorText.value = msg
    showErrorSnackbar(msg)
  } finally {
    invalid.value.loading = false
  }
}

const loadFk = async (force = false) => {
  if (!canShow.value) return
  if (fk.value.loaded && !force) return
  fk.value.loading = true
  errorText.value = ''
  try {
    const data = await getTableForCreateForeignKey()
    const normalized = normalizeRows(data || [])
    fk.value.rows = normalized
    fk.value.columns = buildColumns(normalized)
    fk.value.loaded = true
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'Ошибка при загрузке'
    errorText.value = msg
    showErrorSnackbar(msg)
  } finally {
    fk.value.loading = false
  }
}

const loadTrg = async (force = false) => {
  if (!canShow.value) return
  if (trg.value.loaded && !force) return
  trg.value.loading = true
  errorText.value = ''
  try {
    const data = await getTableForCreateTrigger()
    const normalized = normalizeRows(data || [])
    trg.value.rows = normalized
    trg.value.columns = buildColumns(normalized)
    trg.value.loaded = true
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'Ошибка при загрузке'
    errorText.value = msg
    showErrorSnackbar(msg)
  } finally {
    trg.value.loading = false
  }
}

const loadStat = async (force = false) => {
  if (!canShow.value) return
  if (stat.value.loaded && !force) return
  stat.value.loading = true
  errorText.value = ''
  try {
    const data = await getFuncStat(stat.value.func_name || '')
    const normalized = normalizeRows(data || [])
    stat.value.rows = normalized
    stat.value.columns = buildColumns(normalized)
    stat.value.loaded = true
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'Ошибка при загрузке'
    errorText.value = msg
    showErrorSnackbar(msg)
  } finally {
    stat.value.loading = false
  }
}

const refreshActiveTab = async () => {
  if (activeTab.value === 'invalid') return loadInvalid(true)
  if (activeTab.value === 'fk') return loadFk(true)
  if (activeTab.value === 'trg') return loadTrg(true)
  if (activeTab.value === 'stat') return loadStat(true)
}

const exportActiveTab = async () => {
  if (!canShow.value) return
  exporting.value = true
  try {
    const { downloadFile } = await import('@/services')
    if (activeTab.value === 'invalid') {
      await downloadFile('/common/invalid_objects/export_excel/', 'Инвалидные_объекты', {}, 'xlsx', 'get', true)
      return
    }
    if (activeTab.value === 'fk') {
      await downloadFile('/common/table_for_create_foreign_key/export_excel/', 'Таблицы_FK', {}, 'xlsx', 'get', true)
      return
    }
    if (activeTab.value === 'trg') {
      await downloadFile('/common/table_for_create_trigger/export_excel/', 'Таблицы_Триггеры', {}, 'xlsx', 'get', true)
      return
    }
    if (activeTab.value === 'stat') {
      await downloadFile(
        '/common/func_stat/export_excel/',
        'Статистика_вызовов_функций',
        { params: { func_name: stat.value.func_name || '' } },
        'xlsx',
        'get',
        true
      )
    }
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'Ошибка при экспорте'
    showErrorSnackbar(msg)
  } finally {
    exporting.value = false
  }
}

watch(activeTab, (tab) => {
  if (!canShow.value) return
  if (tab === 'invalid') loadInvalid(false)
  if (tab === 'fk') loadFk(false)
  if (tab === 'trg') loadTrg(false)
  if (tab === 'stat') loadStat(false)
})

onMounted(() => {
  appStore.getUserData().finally(() => loadInvalid(false))
})
</script>

<style scoped lang="scss">
.invalid-objects {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__title {
    font-size: 22px;
    font-weight: 600;
    color: #111827;
  }

  &__banner {
    padding: 12px 14px;
  }

  &__error-title {
    font-weight: 600;
    margin-bottom: 4px;
  }

  &__error-text {
    white-space: pre-wrap;
    word-break: break-word;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 12px;
    opacity: 0.95;
  }

  &__stat-controls {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;
    align-items: end;
    margin-bottom: 12px;
    max-width: 720px;
  }
}

.invalid-objects :deep(.q-table tbody td) {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>

